import { useCallback, useEffect, useRef, useState } from "react"
import {
  Animated,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { Image } from "expo-image"
import * as NavigationBar from "expo-navigation-bar"
import { useFocusEffect, useLocalSearchParams } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { SidekickView } from "src/components/SidekickView"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useStatusBarCompensation } from "src/hooks/useStatusBarCompensation"
import { backToRoot } from "src/utils/routerHelpers"

export default function ImageViewerScreen() {
  const { imageUrl } = useLocalSearchParams<{
    imageUrl?: string | string[]
  }>()
  const resolvedImageUrl = Array.isArray(imageUrl) ? imageUrl[0] : imageUrl
  const { theme, navTheme } = useAppTheme()
  const { colors } = theme
  const insets = useSafeAreaInsets()
  const { effectiveTopInset } = useStatusBarCompensation()
  const { width, height } = useWindowDimensions()
  const isLandscape = width > height

  const controlsOpacity = useRef(new Animated.Value(1)).current

  const [controlsVisible, setControlsVisible] = useState(true)
  const [isClosing, setIsClosing] = useState(false)

  // Unlock orientation for image viewer
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.unlockAsync().catch(() => {})
      // Don't re-lock on cleanup to prevent jitter when auto-navigating between media
    }, []),
  )

  // Handle Android navigation bar
  useEffect(() => {
    if (Platform.OS === "android") {
      NavigationBar.setVisibilityAsync("hidden")
      NavigationBar.setBehaviorAsync("overlay-swipe")
      NavigationBar.setBackgroundColorAsync("#000000")
      NavigationBar.setButtonStyleAsync("light")
    }

    return () => {
      if (Platform.OS === "android") {
        NavigationBar.setVisibilityAsync("visible")
        NavigationBar.setBackgroundColorAsync(theme.colors.background)
        NavigationBar.setButtonStyleAsync(navTheme.dark ? "light" : "dark")
      }
    }
  }, [theme.colors.background, navTheme.dark])

  // Toggle controls visibility when tapping the image
  const handleContentPress = useCallback(() => {
    const newVisible = !controlsVisible
    setControlsVisible(newVisible)
    Animated.timing(controlsOpacity, {
      toValue: newVisible ? 1 : 0,
      duration: 300,
      useNativeDriver: true,
    }).start()
  }, [controlsVisible, controlsOpacity])

  if (!resolvedImageUrl) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Unable to load image.</Text>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <StatusBar style="light" hidden={!controlsVisible} />

      {/* Close button - above SidekickView */}
      <Animated.View
        style={[
          styles.closeButton,
          {
            top: effectiveTopInset + 22,
            right: insets.right + 12,
            opacity: controlsOpacity,
          },
          Platform.OS === "web" && ({ position: "fixed" } as any),
        ]}
        pointerEvents={controlsVisible ? "auto" : "none"}
      >
        <TouchableOpacity
          onPress={async () => {
            // Unmount AvatarOverlay if in landscape to prevent crash during orientation change (APP-738)
            if (isLandscape) {
              setIsClosing(true)
              // Wait for React to unmount the component
              await new Promise((resolve) => setTimeout(resolve, 0))
            }
            backToRoot()
          }}
          style={[styles.closeButtonInner, { backgroundColor: colors.buttonBackground }]}
          accessibilityRole="button"
          accessibilityLabel="Close image viewer"
        >
          <MaterialIcons name="close" size={20} color={colors.buttonText} />
        </TouchableOpacity>
      </Animated.View>

      {!isClosing && (
        <SidekickView
          showAvatar
          autoShowAvatarOnSpeech
          showCaptions
          showCCToggle
          showButtonCluster
          compactButtonCluster
          controlsOpacity={controlsOpacity}
          controlsVisible={controlsVisible}
          onContentPress={handleContentPress}
          riveOwner="video"
          controlsConfig={{ type: "none" }}
        >
          {/* Image content */}
          <View style={styles.imageContainer}>
            <Image
              source={{ uri: resolvedImageUrl }}
              style={styles.image}
              contentFit="contain"
              transition={300}
            />
          </View>
        </SidekickView>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#000000",
  },
  errorContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "#000000",
  },
  errorText: {
    color: "#FFFFFF",
    textAlign: "center",
  },
  closeButton: {
    position: "absolute",
    zIndex: 2100,
  },
  closeButtonInner: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },
  imageContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  image: {
    width: "100%",
    height: "100%",
  },
})
